import { useState } from 'react';
import { Link } from 'react-router-dom';
import { getTrendingLocutores, getTopLocutores, sistemaLocutoresStats } from '@/mocks/sistemaLocutores';
import type { LocutorPerfil } from '@/mocks/sistemaLocutores';

const tabs = [
  { key: 'trending', label: 'Tendencia', icon: 'ri-fire-line' },
  { key: 'top', label: 'Top Locutores', icon: 'ri-trophy-line' },
  { key: 'acciones', label: 'Acciones', icon: 'ri-flashlight-line' },
  { key: 'reglas', label: 'Reglas', icon: 'ri-scales-line' },
] as const;
type TabKey = (typeof tabs)[number]['key'];

export default function SistemaLocutoresSection() {
  const [activeTab, setActiveTab] = useState<TabKey>('trending');
  const trending = getTrendingLocutores();
  const top = getTopLocutores();

  return (
    <section className="py-12 md:py-16 bg-background-50">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-500"></span>
              </span>
              <span className="text-[10px] font-extrabold text-rose-600 tracking-widest uppercase bg-rose-100 px-2.5 py-1 rounded-full">
                SISTEMA DE LOCUTORES
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">
              Locutores que son Estrellas
            </h2>
            <p className="text-sm text-foreground-500 mt-2 max-w-lg">
              Descubrí a las voces que están marcando tendencia. Seguí a tus locutores favoritos independientemente de la radio.
            </p>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            {[
              { value: sistemaLocutoresStats.totalLocutores.toLocaleString(), label: 'Locutores' },
              { value: sistemaLocutoresStats.trendingCount.toString(), label: 'En Tendencia' },
              { value: sistemaLocutoresStats.onAirNow.toString(), label: 'Al Aire' },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <span className="text-xl font-extrabold text-foreground-950 leading-none block">{stat.value}</span>
                <span className="text-[10px] text-foreground-400 uppercase tracking-wider">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-6 overflow-x-auto pb-1">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-all ${
                activeTab === tab.key
                  ? 'bg-foreground-950 text-background-50'
                  : 'bg-background-100 text-foreground-500 hover:text-foreground-700 hover:bg-background-200'
              }`}
            >
              <i className={`${tab.icon} text-sm`}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab: Trending */}
        {activeTab === 'trending' && (
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {trending.map((locutor) => (
                <LocutorMiniCard key={locutor.id} locutor={locutor} badge="trending" />
              ))}
            </div>
          </div>
        )}

        {/* Tab: Top */}
        {activeTab === 'top' && (
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {top.slice(0, 3).map((locutor, i) => (
                <LocutorMiniCard key={locutor.id} locutor={locutor} badge="top" rank={i + 1} />
              ))}
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {top.slice(3, 6).map((locutor, i) => (
                <LocutorMiniCard key={locutor.id} locutor={locutor} badge="top" rank={i + 4} />
              ))}
            </div>
          </div>
        )}

        {/* Tab: Acciones */}
        {activeTab === 'acciones' && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { icon: 'ri-user-add-line', title: 'Seguir', desc: 'Seguí a tus locutores favoritos. Sus actualizaciones aparecen en tu feed.', color: 'from-rose-500 to-rose-400' },
              { icon: 'ri-share-forward-line', title: 'Compartir', desc: 'Compartí el perfil de un locutor con tu comunidad. Hacé crecer su alcance.', color: 'from-amber-500 to-amber-400' },
              { icon: 'ri-chat-1-line', title: 'Comentar', desc: 'Dejá tu mensaje en sus publicaciones. La interacción directa construye comunidad.', color: 'from-emerald-500 to-emerald-400' },
              { icon: 'ri-user-star-line', title: 'Invitar al Club', desc: 'Invitá a un locutor a tu club de fans. Reconocé su talento.', color: 'from-sky-500 to-sky-400' },
            ].map((accion, i) => (
              <div key={i} className="bg-white rounded-xl border border-foreground-100 p-5 text-center group hover:shadow-sm transition-all cursor-default">
                <div className={`w-12 h-12 mx-auto rounded-xl bg-gradient-to-br ${accion.color} flex items-center justify-center mb-3`}>
                  <i className={`${accion.icon} text-white text-xl`}></i>
                </div>
                <h3 className="font-bold text-foreground-950 text-sm">{accion.title}</h3>
                <p className="text-xs text-foreground-500 mt-1.5 leading-relaxed">{accion.desc}</p>
              </div>
            ))}
          </div>
        )}

        {/* Tab: Reglas */}
        {activeTab === 'reglas' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { title: 'Los oyentes pueden seguir locutores independientemente de la radio', desc: 'Un locutor construye su propia audiencia. Si cambia de radio, sus seguidores lo siguen. Si la radio cierra, el locutor mantiene su comunidad intacta.', icon: 'ri-user-heart-line', color: 'from-rose-500 to-rose-400' },
              { title: 'Los locutores son estrellas de la plataforma', desc: 'Perfil completo con foto, portada, biografía, redes sociales, programas, seguidores, ranking e insignias. Todo lo que un locutor necesita para brillar.', icon: 'ri-star-line', color: 'from-amber-500 to-amber-400' },
              { title: 'Ranking y tendencias actualizados en tiempo real', desc: 'El sistema detecta automáticamente qué locutores están tendencia y los muestra primero. El ranking se actualiza según actividad y engagement.', icon: 'ri-line-chart-line', color: 'from-emerald-500 to-emerald-400' },
              { title: 'Acciones sociales que generan comunidad', desc: 'Seguir, compartir, comentar e invitar al club. Cuatro acciones que convierten oyentes pasivos en fans activos.', icon: 'ri-group-line', color: 'from-sky-500 to-sky-400' },
            ].map((regla, i) => (
              <div key={i} className="bg-white rounded-xl border border-foreground-100 p-5 flex items-start gap-4">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${regla.color} flex items-center justify-center flex-shrink-0`}>
                  <i className={`${regla.icon} text-white text-lg`}></i>
                </div>
                <div>
                  <h4 className="font-bold text-foreground-950 text-sm leading-snug">{regla.title}</h4>
                  <p className="text-xs text-foreground-500 mt-1 leading-relaxed">{regla.desc}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-8 text-center">
          <Link
            to="/sistema-locutores"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-xl transition-all cursor-pointer text-sm"
          >
            <i className="ri-user-voice-line"></i>
            Ver todos los locutores
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}

function LocutorMiniCard({
  locutor,
  badge,
  rank,
}: {
  locutor: LocutorPerfil;
  badge: 'trending' | 'top';
  rank?: number;
}) {
  return (
    <Link
      to={`/host/${locutor.slug}`}
      className="group bg-white rounded-xl border border-foreground-100 overflow-hidden hover:shadow-sm transition-all cursor-pointer flex"
    >
      {/* Rank / Badge left accent */}
      <div className={`w-2 flex-shrink-0 bg-gradient-to-b ${
        badge === 'trending' ? 'from-rose-500 to-rose-400' : 'from-amber-500 to-amber-400'
      }`}></div>
      <div className="flex-1 p-4">
        <div className="flex items-start gap-3">
          <div className="relative flex-shrink-0">
            <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-background-100">
              <img src={locutor.photoUrl} alt={locutor.name} className="w-full h-full object-cover" />
            </div>
            {locutor.status === 'on_air' && (
              <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-red-500 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap flex items-center gap-0.5">
                <span className="w-1 h-1 rounded-full bg-white animate-pulse"></span>
                EN VIVO
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <h3 className="font-semibold text-foreground-950 text-sm group-hover:text-accent-600 transition-colors truncate">
                {locutor.name}
              </h3>
              {locutor.verified && (
                <span className="w-4 h-4 flex items-center justify-center rounded-full bg-accent-500 text-background-50 flex-shrink-0">
                  <i className="ri-verified-badge-fill text-[10px]"></i>
                </span>
              )}
            </div>
            <p className="text-xs text-foreground-500 mt-0.5">{locutor.username}</p>
            <div className="flex items-center gap-2 mt-2 text-[10px] text-foreground-400">
              <span className="flex items-center gap-1">
                <i className="ri-group-line"></i>
                {locutor.followers >= 1000 ? `${(locutor.followers / 1000).toFixed(1)}K` : locutor.followers}
              </span>
              <span>·</span>
              <span className="flex items-center gap-1">
                <i className="ri-map-pin-line"></i>
                {locutor.city}
              </span>
              <span>·</span>
              <span className="flex items-center gap-1 text-foreground-500">
                <i className="ri-trophy-line text-[10px]"></i>
                #{locutor.rankingPosition}
              </span>
            </div>
            {/* Specialties */}
            <div className="flex items-center gap-1 mt-1.5 flex-wrap">
              {locutor.specialties.slice(0, 2).map((spec, i) => (
                <span key={i} className="text-[9px] bg-background-100 text-foreground-500 px-1.5 py-0.5 rounded-full">
                  {spec}
                </span>
              ))}
            </div>
          </div>
          {rank && (
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
              <span className="text-xs font-extrabold text-amber-700">#{rank}</span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}